import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  CLOUDS,
  FOG_TYPES,
  FRONTS,
  PRESSURE_FEATURES,
} from "@nav-games/weather";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface SynopticProps {
  onNavigate: (to: string) => void;
}

const MODULE = "weather";
const GAME = "synoptic";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

type Topic = "fronts" | "pressure" | "fog" | "clouds";

interface Choice {
  id: string;
  label: string;
  topic: Topic;
}

interface Card {
  topic: Topic;
  scenario: string;
  answerId: string;
  choices: readonly Choice[];
}

function shuffled<T>(arr: readonly T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }
  return out;
}

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function buildCard(): Card {
  const topic = pick(["fronts", "pressure", "fog", "clouds"] as Topic[]);

  if (topic === "fronts") {
    const f = pick(FRONTS);
    const scenarios = [
      `Cloud sequence overhead: cirrus, then cirrostratus with a halo round the sun, then thickening altostratus and finally nimbostratus + steady rain. Which front type?`,
      `Sharp wind veer from SW to NW, sudden heavy shower with thunder, pressure rises rapidly behind it. Which front type?`,
      `Long lead-in of rain followed by showers, no warm-sector break, often the wettest feature on the chart. Which front type?`,
    ];
    const scenario =
      f.id === "warm"
        ? scenarios[0]!
        : f.id === "cold"
          ? scenarios[1]!
          : scenarios[2]!;
    const choices = shuffled(
      FRONTS.map((x) => ({
        id: x.id,
        label: x.name,
        topic: "fronts" as Topic,
      })),
    ).slice(0, CHOICES_PER_CARD);
    if (!choices.find((c) => c.id === f.id)) choices[0] = { id: f.id, label: f.name, topic: "fronts" };
    return {
      topic,
      scenario,
      answerId: f.id,
      choices: shuffled(choices),
    };
  }

  if (topic === "pressure") {
    const p = pick(PRESSURE_FEATURES);
    const scenario = `On the synoptic chart: ${p.isobarPattern.toLowerCase()} Which feature?`;
    const choices = shuffled(
      PRESSURE_FEATURES.map((x) => ({
        id: x.id,
        label: x.name,
        topic: "pressure" as Topic,
      })),
    ).slice(0, CHOICES_PER_CARD);
    if (!choices.find((c) => c.id === p.id))
      choices[0] = { id: p.id, label: p.name, topic: "pressure" };
    return {
      topic,
      scenario,
      answerId: p.id,
      choices: shuffled(choices),
    };
  }

  if (topic === "fog") {
    const f = pick(FOG_TYPES);
    const scenario = `Conditions: ${f.conditions.toLowerCase().replace(/\.$/, "")}. ${f.summary} What type of fog?`;
    const pool = shuffled(
      FOG_TYPES.map((x) => ({
        id: x.id,
        label: x.name,
        topic: "fog" as Topic,
      })),
    );
    const choices = pool.slice(0, CHOICES_PER_CARD);
    if (!choices.find((c) => c.id === f.id))
      choices[0] = { id: f.id, label: f.name, topic: "fog" };
    return {
      topic,
      scenario,
      answerId: f.id,
      choices: shuffled(choices),
    };
  }

  // clouds
  const c = pick(CLOUDS);
  const scenario = `${c.appearance} What cloud is this?`;
  const pool = shuffled(
    CLOUDS.map((x) => ({
      id: x.id,
      label: x.name,
      topic: "clouds" as Topic,
    })),
  );
  const choices = pool.slice(0, CHOICES_PER_CARD);
  if (!choices.find((ch) => ch.id === c.id))
    choices[0] = { id: c.id, label: c.name, topic: "clouds" };
  return {
    topic,
    scenario,
    answerId: c.id,
    choices: shuffled(choices),
  };
}

function lookupRevealText(card: Card): { headline: string; detail: string } {
  if (card.topic === "fronts") {
    const f = FRONTS.find((x) => x.id === card.answerId)!;
    return {
      headline: f.name,
      detail: `Wind: ${f.windShift} Pressure: ${f.pressure} Precipitation: ${f.precipitation}`,
    };
  }
  if (card.topic === "pressure") {
    const p = PRESSURE_FEATURES.find((x) => x.id === card.answerId)!;
    return {
      headline: p.name,
      detail: `Weather: ${p.weather} Movement: ${p.movement}`,
    };
  }
  if (card.topic === "fog") {
    const f = FOG_TYPES.find((x) => x.id === card.answerId)!;
    return {
      headline: f.name,
      detail: `${f.formation} Clears: ${f.clearance}`,
    };
  }
  const cl = CLOUDS.find((x) => x.id === card.answerId)!;
  return {
    headline: `${cl.name} (${cl.abbreviation})`,
    detail: `Altitude: ${cl.altitude}. Weather signal: ${cl.weatherSignal}`,
  };
}

export function Synoptic({ onNavigate }: SynopticProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: CARDS_PER_SESSION,
      }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (id: string) => {
      if (chosen || result) return;
      const ok = id === card.answerId;
      const attempt: Omit<Attempt, "module"> = {
        char: `${card.topic}-${card.answerId}`,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(id);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/reference")}
      />
    );
  }

  const revealed = chosen !== null;
  const reveal = revealed ? lookupRevealText(card) : null;
  const topicLabel: Record<Topic, string> = {
    fronts: "Front type",
    pressure: "Pressure feature",
    fog: "Fog type",
    clouds: "Cloud",
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "weather" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION} · synoptic weather
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{topicLabel[card.topic]}?</h3>
        <p style={{ marginBottom: 12 }}>{card.scenario}</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c.id === card.answerId;
            const isChosen = c.id === chosen;
            return (
              <button
                key={c.id}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c.id)}
                style={{
                  fontSize: 14,
                  padding: "12px 6px",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {revealed && reveal && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.answerId
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.answerId
                ? `✓ Correct — ${reveal.headline}.`
                : `✗ Not quite — ${reveal.headline}.`}
            </p>
            <p className="small" style={{ margin: "8px 0 0" }}>
              {reveal.detail}
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/reference")}>
        ← Back to Reference
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Reference
        </button>
      </div>
    </>
  );
}
